// React imports
import React from 'react';

// Third-party library imports
import {
    filter,
    flatten,
    forEach,
    get,
    includes,
    map,
    orderBy,
    some
} from 'lodash-es';
import {
    all,
    call,
    cancelled,
    delay,
    fork,
    put,
    select,
    takeEvery,
    takeLatest,
} from 'redux-saga/effects';
import { fetchAuthSession } from 'aws-amplify/auth';

// Constants
import {
    API_NAME,
    maxAPIRefetchCount,
    refetchAPIDelay,
} from '../../config/config';
import { ReportType } from '../../constants/reportSortAndFilters';

// Utility functions
import {
    checkShouldRequestRefetch,
    getRegionallyApiResponse,
    getRegionConfigFromList
} from '../../utils/commonFunctions';
import { DynamicObject } from '../../utils/commonInterfaces';
import {
    getOrganisationActionUrl,
    getSelectedUserOrganisation
} from '../organisations/organisationSlice';
import {
    getSelectedUserCompany,
    getUserCompanies
} from '../companies/companySlice';
import {
    getRegionKeyConfig,
    getRegionSettingsConfig
} from '../auth/authSlice';

// Redux actions
import { _queries as queries } from '../../graphql/queries.graphql';
import { getDashboardsSuccessAction } from '../dashboards/dashboardSlice';
import {
    deleteAppliedFiltersViewAction,
    deleteAttachmentAction,
    downloadToExcelAction,
    fetchPageAppliedFiltersViewListRequestAction,
    getAttachmentPresignedUrlAction,
    getCustomFieldsFilterListByTypes,
    getCustomFieldsFilterListByTypesForOrganisation,
    getDemoOptionsConfigAction,
    getPresignedUrlDownloadReportReadyAction,
    saveAppliedFiltersViewAction,
    updateDemoOptionsConfigAction,
} from './commonSlice';

// Types
import {
    CompanyCustomFieldConfigure,
    CompanyUserRole
} from '../companies/types';
import { Organisation } from '../organisations/types';

// API
import client, {
    post,
    get as amplifyGet,
    delApi
} from 'src/api';

/**
 * Function called for saving the applied filters view to db.
 */
function* handleSaveAppliedFiltersView({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        // To call async functions, use redux-saga's `call()`.
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/pageView/view',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();
        res.IsSuccess = true;
        if (callback) {
            callback(res);
        }
    }
    catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }


        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
        //if (callback) {
        //    const returnData = get(err.response, 'data')
        //        ? err.response.data
        //        : { Messages: [err.message] };
        //    returnData.IsSuccess = false;
        //    callback(returnData);
        //}
        //if (err instanceof Error) {
        //    console.log('Error', err);
        //} else {
        //    console.error('An unknown error occured.');
        //}
    }
}

/**
 * Function called for deleting the applied filters view from db.
 */
function* handleDeleteAppliedFiltersView({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        yield call(delApi, `/pageView/view`, payload);

        const res: DynamicObject = {
            IsSuccess: true
        }

        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }


        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
        //if (callback) {
        //    const returnData = get(err.response, 'data')
        //        ? err.response.data
        //        : { Messages: [err.message] };
        //    returnData.IsSuccess = false;
        //    callback(returnData);
        //}
        //if (err instanceof Error) {
        //    console.log('Error', err);
        //} else {
        //    console.error('An unknown error occured.');
        //}
    }
}

/**
 * Function called for fetching the Page Views list from the api.
 */
function* handleFetchPageAppliedFiltersViewListRequestAction({
    payload: sagaPayload,
}: any) {
    const { pageName, isDashboard, notSameCompany, callback } = sagaPayload;
    const abortController = new AbortController();

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_PAGE_VIEWS,
                variables: {
                    PageName: pageName,
                }
            }
        );

        const { Views: PageViews } = get(res.data, 'GetPageViews');

        if (PageViews) {
            if (callback) {
                callback(PageViews);
            }
        }

        if (isDashboard) {
            yield put(
                getDashboardsSuccessAction({ data: PageViews, notSameCompany })
            );
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        if (callback) {
            callback([]);
        }
    } finally {
        const isCancelled: boolean = yield cancelled();

        if (isCancelled) {
            abortController.abort();
        }
    }
}

/**
 * Function called for starting the data extraction for the items needed in excel file.
 */
function* handleDownloadToExcel({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;
    payload.Type = payload.Type || ReportType.Page;
    let ApiUrl: string | undefined = get(payload, 'ApiUrl');
    
    if (!ApiUrl) {
        ApiUrl = yield select(
            getOrganisationActionUrl
        );
    }

    try {
        yield call(
            getRegionallyApiResponse,
            ApiUrl,
            '/report/excel',
            'POST',
            payload
        );

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.', err);
        }
    }
}

/**
 * Function called for getting the presigned URL which then is used
 * for downloading the excel report stored in S3.
 */
function* handleGetPresignedUrlForReportReady({ payload: sagaPayload }: any) {
    const { payload, callback, refetchCount } = sagaPayload;
    const Region = get(payload, 'Region');

    try {
        let apiUrl: string | undefined = undefined;

        if (Region) {
            const regionKeyConfig: DynamicObject[] = yield select(
                getRegionKeyConfig
            );

            const regionSettingsConfig: DynamicObject[] = yield select(
                getRegionSettingsConfig
            );

            const configUsed = getRegionConfigFromList(
                Region,
                regionKeyConfig,
                regionSettingsConfig
            );

            apiUrl = get(configUsed, 'Url');
        }

        let res: DynamicObject;

        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = currentSession.tokens.accessToken;

            const response: Response = yield fetch(
                `${apiUrl}/report/excel?ReportJobId=${get(payload, 'EntityKey')}`,
                {
                    method: 'GET',
                    headers: { Authorization: accessToken }
                }
            );

            res = yield response.json();
        }
        else {
            const { body } = yield call(amplifyGet, 
                {
                    apiName: API_NAME,
                    path: '/report/excel',
                    options: {
                        queryParams: {
                            ReportJobId: get(payload, 'EntityKey')
                        }
                    }
                }
            );
            res = yield body.json();
        }

        if (callback) {
            res.IsSuccess = true;

            callback(res);
        }
    } catch (err) {
        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            yield delay(refetchAPIDelay);
            yield put(getPresignedUrlDownloadReportReadyAction(payload, callback, refetchCount + 1));
        }
        else {
            if (callback) {
                let returnData: any;

                if (err instanceof Error && 'response' in err && (err as any).response.body) {
                    returnData = JSON.parse((err as any).response.body);
                } else if (err instanceof Error) {
                    returnData = { Messages: [err.message] };
                } else {
                    returnData = { Messages: ['An unknown error occurred.'] };
                }

                returnData.IsSuccess = false;
                callback(returnData);
            }

            if (err instanceof Error) {
                console.log('Error', err);
            } else {
                console.error('An unknown error occurred.', err);
            }
        }
    }
}

/**
 * Function called for getting the custom fields filter list for a specific page.
 */
function* handleGetCustomFieldsFilterListByTypes({
    payload: sagaPayload,
}: any) {
    const { customFieldTypes, callback } = sagaPayload;

    try {
        const selectedUserCompany: DynamicObject = yield select(getSelectedUserCompany);
        const companyCustomFields = get(
            selectedUserCompany,
            'Company.CustomFieldsConfigure'
        );
        
        const customFieldsByType: CompanyCustomFieldConfigure[] = [];
        forEach (
            companyCustomFields,
            (customField: CompanyCustomFieldConfigure) => {
                if (includes(customFieldTypes, customField.Type)) {
                    customFieldsByType.push(customField);
                }
            }
        );

        const customFieldsByNumberSorted = orderBy(
            customFieldsByType,
            ['Number'],
            ['asc']
        );

        const sortedCustomFieldsByType = orderBy(
            customFieldsByNumberSorted,
            function (item) {
                return customFieldTypes.indexOf(item.Type);
            }
        );

        const res: DynamicObject = {
            CustomFields: sortedCustomFieldsByType,
        };

        if (callback) {
            res.IsSuccess = true;
            callback(res);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.', err);
        }
        //if (callback) {
        //    const returnData = get(err.response, 'data')
        //        ? err.response.data
        //        : { Messages: [err.message] };
        //    returnData.IsSuccess = false;
        //    callback(returnData);
        //}

        //if (err instanceof Error) {
        //    console.log('Error', err);
        //} else {
        //    console.error('An unknown error occured.', err);
        //}
    }
}

function* handleGetCustomFieldsFilterListByTypesForOrganisation({
    payload: sagaPayload,
}: any) {
    const { companyIds, customFieldTypes, callback } = sagaPayload;

    try {
        const selectedUserOrganisation: Organisation = yield select(getSelectedUserOrganisation);
        const userCompanies: CompanyUserRole[] = yield select(getUserCompanies);
        const orgUserCompanies = filter(userCompanies, uc => uc.Company.OrganisationId === get(selectedUserOrganisation, 'OrganisationId'));
        const orgWorkflowUserCompanies = orgUserCompanies.filter(oc => companyIds.includes(oc.Company.CompanyId));
        const companyCustomFields = flatten(map(orgWorkflowUserCompanies, uc => get(uc, 'Company.CustomFieldsConfigure')));

                
        let customFieldsByType: CompanyCustomFieldConfigure[] = [];
        forEach(
           companyCustomFields,
           (customField: CompanyCustomFieldConfigure) => {
               if (includes(customFieldTypes, customField.Type)
                   && !some(customFieldsByType, cf => cf.FieldName === customField.FieldName)) {
                   customFieldsByType.push(customField);
               }
           }
        );
        
        const customFieldsByNumberSorted = orderBy(
            customFieldsByType,
            ['Number'],
            ['asc']
        );

        const sortedCustomFieldsByType = orderBy(
            customFieldsByNumberSorted,
            function (item) {
                return customFieldTypes.indexOf(item.Type);
            }
        );
        
        const res: DynamicObject = {
            CustomFields: sortedCustomFieldsByType,
        };

        if (callback) {
            res.IsSuccess = true;
            callback(res);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.', err);
        }
        //if (callback) {
        //    const returnData = get(err.response, 'data')
        //        ? err.response.data
        //        : { Messages: [err.message] };
        //    returnData.IsSuccess = false;
        //    callback(returnData);
        //}

        //if (err instanceof Error) {
        //    console.log('Error', err);
        //} else {
        //    console.error('An unknown error occured.', err);
        //}
    }
}


/**
 * Function called for getting the presigned url for uploading an attachment.
 */
function* handleGetAttachmentPresignedUrl({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        // To call async functions, use redux-saga's `call()`.
        const apiUrl: string | undefined = yield select(getOrganisationActionUrl);
        const res: DynamicObject = yield call(
            getRegionallyApiResponse,
            apiUrl,
            '/document/attachtoresource',
            'POST',
            payload
        );

        res.IsSuccess = true;

        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }


        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
        //if (callback) {
        //    const returnData = get(err.response, 'data')
        //        ? err.response.data
        //        : { Messages: [err.message] };
        //    returnData.IsSuccess = false;
        //    callback(returnData);
        //}
        //if (err instanceof Error) {
        //    console.log('Error', err);
        //} else {
        //    console.error('An unknown error occured.');
        //}
    }
}

/**
 * Function called for deleting an attachment.
 */
function* handleDeleteAttachment({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        let apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyApiResponse,
            apiUrl,
            '/document/attachtoresource',
            'DELETE',
            payload
        );

        res.IsSuccess = true;

        if (callback) {
            callback(res);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }


        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

let refetchCount = 0;
/**
 * Function called for getting the demo options config from the json file.
 */
function* handleGetDemoOptionsConfig() {
    try {
        const jsonResponse: DynamicObject = yield call(
            fetch,
            `${window.location.origin}/demo-options-config.json`
        );
        const demoOptionsConfig: DynamicObject[] = yield jsonResponse.json();
        refetchCount = 0;

        yield put(updateDemoOptionsConfigAction(demoOptionsConfig));
    }
    catch (err) {
        console.log('err: ', err);

        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(getDemoOptionsConfigAction());
        }
        else {
            console.log('Error trying to get demo options config', err);
        }
    }
}

function* watchSaveAppliedFiltersView() {
    yield takeLatest(
        saveAppliedFiltersViewAction.type,
        handleSaveAppliedFiltersView
    );
}

function* watchDeleteAppliedFiltersView() {
    yield takeLatest(
        deleteAppliedFiltersViewAction.type,
        handleDeleteAppliedFiltersView
    );
}

function* watchFetchPageAppliedFiltersViewListRequest() {
    yield takeEvery(
        fetchPageAppliedFiltersViewListRequestAction.type,
        handleFetchPageAppliedFiltersViewListRequestAction
    );
}

function* watchDownloadToExcel() {
    yield takeLatest(
        downloadToExcelAction.type,
        handleDownloadToExcel
    );
}

function* watchGetPresignedUrlForReportReady() {
    yield takeLatest(
        getPresignedUrlDownloadReportReadyAction.type,
        handleGetPresignedUrlForReportReady
    );
}

function* watchGetCustomFieldsFilterListByTypes() {
    yield takeLatest(
        getCustomFieldsFilterListByTypes.type,
        handleGetCustomFieldsFilterListByTypes
    );
}

function* watchGetCustomFieldsFilterListByTypesForOrganisation() {
    yield takeLatest(
        getCustomFieldsFilterListByTypesForOrganisation.type,
        handleGetCustomFieldsFilterListByTypesForOrganisation
    );
}

function* watchGetAttachmentPresignedUrl() {
    yield takeLatest(
        getAttachmentPresignedUrlAction.type,
        handleGetAttachmentPresignedUrl
    );
}

function* watchDeleteAttachment() {
    yield takeLatest(
        deleteAttachmentAction.type,
        handleDeleteAttachment
    );
}

function* watchGetDemoOptionsConfig() {
    yield takeLatest(
        getDemoOptionsConfigAction.type,
        handleGetDemoOptionsConfig
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* commonSaga() {
    yield all([
        fork(watchSaveAppliedFiltersView),
        fork(watchDeleteAppliedFiltersView),
        fork(watchFetchPageAppliedFiltersViewListRequest),
        fork(watchDownloadToExcel),
        fork(watchGetPresignedUrlForReportReady),
        fork(watchGetCustomFieldsFilterListByTypes),
        fork(watchGetCustomFieldsFilterListByTypesForOrganisation),
        fork(watchGetAttachmentPresignedUrl),
        fork(watchDeleteAttachment),
        fork(watchGetDemoOptionsConfig),
    ]);
}

export default commonSaga;
